import { createFileRoute, getRouteApi, redirect } from "@tanstack/react-router";
import { useState } from "react";

import { VariablesTab } from "../../../components/dev/VariablesTab";
import { SubAgentsTab } from "../../../components/dev/SubAgentsTab";
import { ToolTemplatesTab } from "../../../components/dev/ToolTemplatesTab";
import { EstoqueTemplateTab } from "../../../components/dev/EstoqueTemplateTab";
import { AgentTemplatesTab } from "../../../components/dev/AgentTemplatesTab";
import { AvailableModelsTab } from "../../../components/dev/AvailableModelsTab";
import { InsightsConfigTab } from "../../../components/dev/InsightsConfigTab";
import { ReportsConfigTab } from "../../../components/dev/ReportsConfigTab";
import { WebSearchTab } from "../../../components/dev/WebSearchTab";
import { EstoqueFontesTab } from "../../../components/dev/EstoqueFontesTab";
import { HelenaTab } from "../../../components/dev/HelenaTab";
import { EmailPortaisTab } from "../../../components/dev/EmailPortaisTab";
import { KanbanTab } from "../../../components/dev/KanbanTab";
import type { CurrentUser } from "../../../lib/auth";

export const Route = createFileRoute("/_authed/dev/")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin" && u.permissions?.viewer) {
      throw redirect({ to: "/" });
    }
  },
  component: DevPanel,
});

const _authedRoute = getRouteApi("/_authed");

const ALL_TABS = [
  { id: "variables",         label: "Variáveis Globais",    perm: "dev_variaveis" },
  { id: "subagents",         label: "Sub-Agentes",          perm: "dev_subagentes" },
  { id: "available-models",  label: "Modelos Disponíveis",  perm: "dev_modelos" },
  { id: "insights",          label: "Insights",             perm: "dev_modelos" },
  { id: "reports",           label: "Relatórios",           perm: "dev_modelos" },
  { id: "tool-templates",    label: "Modelos de Tools",     perm: "dev_tools" },
  { id: "estoque-template",  label: "Tool de Estoque",      perm: "dev_tools" },
  { id: "estoque-fontes",    label: "Fontes de estoque",    perm: "dev_tools" },
  { id: "websearch",         label: "Busca na internet",    perm: "dev_tools" },
  { id: "email-portais",     label: "Portais por e-mail",   perm: "dev_tools" },
  { id: "prompt-templates",  label: "Templates de Agentes",  perm: "dev_prompt_templates" },
  { id: "helena",            label: "Portal do Cliente",    perm: "dev_helena" },
  { id: "kanban",            label: "Kanban",               perm: "kanban_total" },
] as const;

type TabId = (typeof ALL_TABS)[number]["id"];

function DevPanel() {
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const user = routeCtx.user;
  const isAdmin = user?.role_name === "Admin";
  const devAdmin = isAdmin || !!user?.permissions?.dev_acesso;

  // dev_modelos_leitura: acesso somente-leitura às abas de modelos (analisar/
  // copiar prompts sem poder editar — a escrita é barrada também no backend).
  const perms = (user?.permissions ?? {}) as Record<string, boolean>;
  const readonlyModels = !devAdmin && !!perms.dev_modelos_leitura;
  const READONLY_TABS = ["tool-templates", "prompt-templates"];

  const visibleTabs = ALL_TABS.filter(
    (t) => devAdmin || !!perms[t.perm] || (readonlyModels && READONLY_TABS.includes(t.id))
  );

  const [tab, setTab] = useState<TabId | null>(visibleTabs[0]?.id ?? null);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Painel de Dev</span>
      </div>

      {visibleTabs.length === 0 ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 13, color: "var(--ink-dim)" }}>Sem sub-abas disponíveis para esta função.</span>
        </div>
      ) : (
        <>
          {/* Sub-nav tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--line)", flexShrink: 0, padding: "0 24px" }}>
            {visibleTabs.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    padding: "10px 16px",
                    fontSize: 13,
                    color: active ? "var(--brand)" : "var(--ink-muted)",
                    borderBottom: active ? "2px solid var(--brand)" : "2px solid transparent",
                    fontWeight: active ? 500 : 400,
                    marginBottom: -1,
                    background: "none",
                    border: "none",
                    borderBottomWidth: 2,
                    borderBottomStyle: "solid",
                    borderBottomColor: active ? "var(--brand)" : "transparent",
                    cursor: "pointer",
                    transition: "color .12s, border-color .12s",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "28px 24px" }}>
            {tab === "variables"        && <VariablesTab />}
            {tab === "subagents"        && <SubAgentsTab />}
            {tab === "available-models" && <AvailableModelsTab />}
            {tab === "insights"         && <InsightsConfigTab />}
            {tab === "reports"          && <ReportsConfigTab />}
            {tab === "tool-templates"   && <ToolTemplatesTab readOnly={readonlyModels && !perms.dev_tools} />}
            {tab === "estoque-template" && <EstoqueTemplateTab />}
            {tab === "estoque-fontes"   && <EstoqueFontesTab />}
            {tab === "websearch"        && <WebSearchTab />}
            {tab === "email-portais"    && <EmailPortaisTab />}
            {tab === "prompt-templates" && <AgentTemplatesTab readOnly={readonlyModels && !perms.dev_prompt_templates} />}
            {tab === "helena"           && <HelenaTab />}
            {tab === "kanban"           && <KanbanTab />}
          </div>
        </>
      )}
    </div>
  );
}
